import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/cliente';
import type { ListaNotificaciones, Notificacion } from '../api/tipos';
import { Icono, type NombreIcono } from '../components/Iconos';
import { Popover } from '../components/Ui';
import { transcurrido } from '../lib/formato';
import { useUi } from './contexto';

const ICONO_TIPO: Record<string, NombreIcono> = {
  asignacion: 'usuario',
  mencion: 'arroba',
  comentario: 'comentario',
  por_aprobar: 'aprobaciones',
  excepcion: 'excepciones',
  estado: 'tablero',
  margen: 'dinero',
  comercial: 'diana',
  personas: 'usuario',
};

export function Notificaciones({ enRiel }: { enRiel?: boolean }) {
  const ui = useUi();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [solo, setSolo] = useState(false);
  const q = useQuery({ queryKey: ['notificaciones'], queryFn: () => api.notificaciones(), refetchInterval: 30_000, retry: false });
  const leer = useMutation({
    mutationFn: (ids?: number[]) => api.leerNotificaciones(ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ['notificaciones'] });
      const previo = qc.getQueryData<ListaNotificaciones>(['notificaciones']);
      if (previo) {
        const items = previo.items.map((n) => (!ids || ids.includes(n.id) ? { ...n, leida: true } : n));
        qc.setQueryData<ListaNotificaciones>(['notificaciones'], { items, no_leidas: items.filter((n) => !n.leida).length });
      }
      return { previo };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previo) qc.setQueryData(['notificaciones'], ctx.previo);
      ui.avisar({ tono: 'error', texto: 'No se pudieron marcar las notificaciones' });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });
  const n = q.data?.no_leidas ?? 0;
  const items = (q.data?.items ?? []).filter((x) => !solo || !x.leida);

  const abrir = (x: Notificacion, cerrar: () => void) => {
    if (!x.leida) leer.mutate([x.id]);
    cerrar();
    if (x.enlace) navegar(x.enlace);
    else if (x.case_id) ui.abrirCaso(x.case_id);
  };

  return (
    <Popover
      ubicacion={enRiel ? 'derecha-fin' : 'abajo-fin'}
      ancho={400}
      etiqueta="Notificaciones"
      className="popover--notificaciones"
      disparador={({ ref, alternar, props, abierto }) => (
        <button
          ref={ref}
          type="button"
          className={`${enRiel ? 'riel__boton' : 'btn-icono btn-icono--barra'} ${abierto ? (enRiel ? 'riel__boton--abierto' : 'btn-icono--activo') : ''}`}
          onClick={alternar}
          aria-label={n ? `Notificaciones, ${n} sin leer` : 'Notificaciones'}
          data-ayuda="Notificaciones"
          data-ayuda-pos={enRiel ? 'derecha' : 'abajo'}
          {...props}
        >
          <Icono nombre="campana" tam={20} />
          {n > 0 && <span className="insignia">{n > 9 ? '9+' : n}</span>}
        </button>
      )}
    >
      {(cerrar) => (
        <div className="notificaciones">
          <header className="notificaciones__cabecera">
            <h2>Notificaciones</h2>
            <label className="interruptor">
              <input type="checkbox" checked={solo} onChange={(e) => setSolo(e.target.checked)} />
              <span className="interruptor__pista" aria-hidden="true" />
              Solo no leídas
            </label>
          </header>
          {q.isPending ? (
            <p className="menu__vacio">Cargando…</p>
          ) : q.isError ? (
            <p className="menu__vacio">No fue posible cargar las notificaciones.</p>
          ) : items.length === 0 ? (
            <div className="notificaciones__vacio">
              <Icono nombre="campana" tam={20} />
              <p>{solo ? 'No tiene notificaciones sin leer.' : 'Aquí verá asignaciones, menciones y casos por aprobar.'}</p>
            </div>
          ) : (
            <ul className="notificaciones__lista" role="list">
              {items.map((x) => (
                <li key={x.id}>
                  <button type="button" className={`notificacion ${x.leida ? '' : 'notificacion--nueva'}`} onClick={() => abrir(x, cerrar)}>
                    <span className={`notificacion__icono notificacion__icono--${x.tipo}`}>
                      <Icono nombre={ICONO_TIPO[x.tipo] ?? 'info'} />
                    </span>
                    <span className="notificacion__texto">
                      <span>{x.texto}</span>
                      <small>
                        {x.case_id && <span className="notificacion__clave">{x.case_id}</span>}
                        {x.case_id && ' · '}hace {transcurrido(x.creado)}
                      </small>
                    </span>
                    {!x.leida && <span className="notificacion__punto" aria-label="Sin leer" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <footer className="notificaciones__pie">
            <button type="button" className="btn btn--fantasma btn--pequeno" disabled={!n || leer.isPending} onClick={() => leer.mutate(undefined)}>
              <Icono nombre="check" /> Marcar todas como leídas
            </button>
          </footer>
        </div>
      )}
    </Popover>
  );
}
